import { Index, Configure, useInstantSearch } from 'react-instantsearch'
import { useEffect, useRef } from 'react'

import { useUserResources } from 'shared/hooks/useUserResources'

import { useTabContext } from 'entry-key/contexts/TabContext'
import { useAnalyticsTags } from 'entry-key/hooks/useAnalyticsTags'
import { useServerContext } from 'entry-key/routes/routes.context'

import { ServicesTabContentInner } from './ServicesTabContentInner'

/**
 * O ServiceTabContent controla quando a tab de Serviços pode processar queries e disparar insights.
 *
 * Regra de negócio: toda nova busca sempre começa pela aba de Produtos.
 * Se o usuário está em Serviços e pesquisa um novo termo, a tab de Produtos
 * é ativada primeiro. A tab de Serviços fica congelada — não processa a query
 * nem dispara insights — até que o usuário clique nela explicitamente.
 *
 * A trava central é o `shouldRenderConfigure`: o <Configure /> só renderiza
 * quando a tab de serviços é a ativa E a query é a esperada.
 *
 * Refs:
 * - hasMountedRef: evita renderizar antes da primeira visita à tab
 * - expectedQueryRef: armazena a query que serviços tem "permissão" para processar
 * - wasServiceTabRef: detecta o momento exato da transição entre tabs
 */

const HITS_PER_PAGE = 36
const MAX_VALUES_PER_FACET = 1000

const ServiceTabContent = () => {
  const { algolia, isAlgoliaAssistedSale } = useServerContext()
  const { indexUiState } = useInstantSearch()
  const { activeTab } = useTabContext()
  const analyticsTags = useAnalyticsTags()
  const { userResources } = useUserResources()

  const storeSlug = userResources?.storeSlug ?? undefined
  const activeServiceIndexName = isAlgoliaAssistedSale
    ? algolia?.assistedSaleServiceIndexName
    : algolia?.serviceIndexName

  const isServiceTab = activeTab === 'services'

  const hasMountedRef = useRef(false)
  const expectedQueryRef = useRef(indexUiState?.query || '')
  const wasServiceTabRef = useRef(false)

  if (isServiceTab) {
    hasMountedRef.current = true
  }

  const assistedSaleFilter = storeSlug
    ? `store_${storeSlug}.isVisibleOnRegion:true`
    : ''

  const regionalFilter = userResources?.region
    ? `regionalAttributes.${userResources.region}.promotionalPrice > 0 AND regionalAttributes.${userResources.region}.available=1 AND regionalAttributes.${userResources.region}.stock.hasStock=1`
    : ''

  const defaultFilter = isAlgoliaAssistedSale
    ? assistedSaleFilter
    : regionalFilter

  const searchQuery = indexUiState?.query || ''

  /**
   * Detecta se a tab de serviços acabou de ser ativada (transição produtos → serviços).
   * Quando isso ocorre, sincroniza a query esperada com a query atual
   * para que o <Configure /> renderize imediatamente com os parâmetros corretos.
   */
  const justActivated = isServiceTab && !wasServiceTabRef.current

  if (justActivated) {
    expectedQueryRef.current = searchQuery
  }

  /**
   * wasServiceTabRef: salva o estado atual da tab para que o
   * justActivated detecte quando o usuário troca de Produtos para Serviços.
   *
   * expectedQueryRef: quando a tab de Serviços não está ativa,
   * atualiza a query esperada para que esteja pronta quando o usuário clicar nela.
   */
  useEffect(() => {
    wasServiceTabRef.current = isServiceTab

    if (!isServiceTab) {
      expectedQueryRef.current = searchQuery
    }
  }, [isServiceTab, searchQuery])

  if (!userResources?.region || !hasMountedRef.current) return null

  /**
   * Guarda contra a corrida de estados.
   * Quando a query muda enquanto a tab de serviços ainda está ativa
   * (antes do useEffect do SearchResultTabs trocar para Produtos),
   * o expectedQueryRef ainda tem a query antiga. A comparação impede
   * que o <Configure /> renderize nesse instante, evitando que
   * insights de serviços disparem com a query nova no momento errado.
   */
  const shouldRenderConfigure =
    isServiceTab && expectedQueryRef.current === searchQuery

  return (
    <Index indexName={activeServiceIndexName} EXPERIMENTAL_isolated>
      {shouldRenderConfigure && (
        <Configure
          facets={['*']}
          filters={defaultFilter}
          hitsPerPage={HITS_PER_PAGE}
          query={searchQuery}
          analyticsTags={analyticsTags}
          clickAnalytics
          maxValuesPerFacet={MAX_VALUES_PER_FACET}
          page={0}
          facetingAfterDistinct
        />
      )}

      <ServicesTabContentInner
        expectedQuery={searchQuery}
        serviceIndexName={activeServiceIndexName}
        isActive={isServiceTab}
        isAssistedSale={!!isAlgoliaAssistedSale}
        storeSlug={storeSlug}
      />
    </Index>
  )
}

export default ServiceTabContent
